import { useRef, useState, useEffect, useCallback } from 'react'

export interface UseTabsScrollReturn {
  scrollContainerRef: React.RefObject<HTMLElement>
  canScrollLeft: boolean
  canScrollRight: boolean
  isHovering: boolean
  startScrolling: (direction: 'left' | 'right') => void
  stopScrolling: () => void
  scrollToEnd: (direction: 'left' | 'right') => void
  updateScrollAvailability: () => void
  handleContainerHover: (hovering: boolean) => void
}

export interface UseTabsScrollOptions {
  scrollStep?: number
  intervalTime?: number
  animationDuration?: number
}

export function useTabsScroll(
  options: UseTabsScrollOptions = {}
): UseTabsScrollReturn {
  const { scrollStep = 2, intervalTime = 20, animationDuration = 300 } = options

  const scrollContainerRef = useRef<HTMLElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const checkScrollAvailability = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
  }, [])

  const startScrolling = useCallback(
    (direction: 'left' | 'right') => {
      const container = scrollContainerRef.current
      if (!container) return

      // Clear any existing interval
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current)
      }

      scrollIntervalRef.current = setInterval(() => {
        const currentScrollLeft = container.scrollLeft
        const maxScrollLeft = container.scrollWidth - container.clientWidth

        if (direction === 'left') {
          if (currentScrollLeft <= 0) {
            if (scrollIntervalRef.current) {
              clearInterval(scrollIntervalRef.current)
              scrollIntervalRef.current = null
            }
            return
          }
          container.scrollLeft = Math.max(0, currentScrollLeft - scrollStep)
        } else {
          if (currentScrollLeft >= maxScrollLeft) {
            if (scrollIntervalRef.current) {
              clearInterval(scrollIntervalRef.current)
              scrollIntervalRef.current = null
            }
            return
          }
          container.scrollLeft = Math.min(
            maxScrollLeft,
            currentScrollLeft + scrollStep
          )
        }

        checkScrollAvailability()
      }, intervalTime)
    },
    [checkScrollAvailability, scrollStep, intervalTime]
  )

  const stopScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
  }, [])

  const scrollToEnd = useCallback(
    (direction: 'left' | 'right') => {
      const container = scrollContainerRef.current
      if (!container) return

      // Stop any ongoing slow scrolling
      stopScrolling()

      const maxScrollLeft = container.scrollWidth - container.clientWidth
      const targetScrollLeft = direction === 'left' ? 0 : maxScrollLeft

      // Use smooth scrolling for the full scroll
      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      })

      // Update scroll availability after animation completes
      setTimeout(() => {
        checkScrollAvailability()
      }, animationDuration)
    },
    [stopScrolling, checkScrollAvailability, animationDuration]
  )

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    checkScrollAvailability()

    const handleScroll = () => {
      checkScrollAvailability()
    }

    const handleResize = () => {
      checkScrollAvailability()
    }

    container.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      stopScrolling()
    }
  }, [checkScrollAvailability, stopScrolling])

  // Expose checkScrollAvailability for external use (e.g., when data changes)
  const updateScrollAvailability = useCallback(() => {
    checkScrollAvailability()
  }, [checkScrollAvailability])

  const handleContainerHover = useCallback((hovering: boolean) => {
    setIsHovering(hovering)
  }, [])

  return {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    isHovering,
    startScrolling,
    stopScrolling,
    scrollToEnd,
    updateScrollAvailability,
    handleContainerHover
  }
}
